/* =============================================
   NORDRC THEME — JAVASCRIPT
   ============================================= */

// ---- Cart Drawer ----
class CartDrawer {
  constructor() {
    this.overlay = document.getElementById('cart-drawer-overlay');
    this.closeBtn = document.getElementById('cart-drawer-close');
    this.backdrop = this.overlay?.querySelector('.cart-drawer-backdrop');
    this.openBtns = document.querySelectorAll('[data-open-cart]');

    if (!this.overlay) return;

    this.openBtns.forEach(btn => btn.addEventListener('click', () => this.open()));
    this.closeBtn?.addEventListener('click', () => this.close());
    this.backdrop?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });

    document.addEventListener('cart:updated', () => this.refresh());
  }

  open() {
    this.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.overlay.setAttribute('aria-hidden', 'false');
    this.closeBtn?.focus();
  }

  close() {
    this.overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    this.overlay.setAttribute('aria-hidden', 'true');
  }

  async refresh() {
    try {
      const res = await fetch('/?section_id=cart-drawer-items');
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newBody = doc.querySelector('.cart-drawer__body');
      const newFoot = doc.querySelector('.cart-drawer__foot');
      const currentBody = this.overlay.querySelector('.cart-drawer__body');
      const currentFoot = this.overlay.querySelector('.cart-drawer__foot');
      if (newBody && currentBody) currentBody.innerHTML = newBody.innerHTML;
      if (newFoot && currentFoot) currentFoot.innerHTML = newFoot.innerHTML;
      if (newBody) this.updateCount(newBody.dataset.itemCount);
    } catch (e) { console.error('Cart refresh failed', e); }
  }

  updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
    });
  }
}

// ---- Money formatting (Shopify's canonical helper) ----
function formatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const formatString = format || '${{amount}}';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimal = decimal == null ? '.' : decimal;
    if (isNaN(number) || number == null) return '0';
    number = (number / 100.0).toFixed(precision);
    const parts = number.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const cents = parts[1] ? decimal + parts[1] : '';
    return dollars + cents;
  }
  const match = formatString.match(placeholderRegex);
  let value;
  switch (match && match[1]) {
    case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
    case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
    case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
    case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'", '.'); break;
    default: value = formatWithDelimiters(cents, 2);
  }
  return formatString.replace(placeholderRegex, value);
}

// ---- Keep the add-to-cart button price in sync with quantity ----
class AtcPrice {
  constructor() {
    document.querySelectorAll('[data-atc-btn][data-price-tpl]').forEach(btn => {
      const form = btn.closest('form');
      const qtyInput = form?.querySelector('[data-qty-input]');
      if (!qtyInput) return;
      const update = () => {
        const qty = Math.max(1, parseInt(qtyInput.value) || 1);
        const total = (parseInt(btn.dataset.unitPrice) || 0) * qty;
        const label = btn.dataset.priceTpl.replace('%PRICE%', formatMoney(total, btn.dataset.moneyFormat));
        btn.dataset.label = label;
        if (!btn.disabled) btn.innerHTML = label;
      };
      qtyInput.addEventListener('change', update);
      update();
    });
  }
}

// ---- Variant selection ----
class VariantSelector {
  constructor() {
    document.querySelectorAll('[data-add-to-cart-form]').forEach(form => {
      const radios = form.querySelectorAll('[data-variant-selector]');
      if (!radios.length) return;
      const idInput = form.querySelector('input[type=hidden][name=id]');
      const atcBtn  = form.querySelector('[data-atc-btn]');
      const qtyInput = form.querySelector('[data-qty-input]');

      radios.forEach(radio => radio.addEventListener('change', () => {
        if (!radio.checked) return;
        // Move the selected highlight
        form.querySelectorAll('.variant-btn').forEach(b => b.classList.remove('is-selected'));
        radio.closest('.variant-btn')?.classList.add('is-selected');
        // Point the cart at the chosen variant
        if (idInput) idInput.value = radio.value;
        // Reflect availability + price on the button
        const available = radio.dataset.variantAvailable === 'true';
        if (atcBtn) {
          atcBtn.disabled = !available;
          if (!available) {
            atcBtn.innerHTML = atcBtn.dataset.soldOut || 'Sold out';
          } else {
            atcBtn.dataset.unitPrice = radio.dataset.variantPrice;
            qtyInput?.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }));
    });
  }
}

// ---- Add to Cart ----
class AddToCart {
  constructor() {
    document.addEventListener('submit', async e => {
      const form = e.target;
      if (!form.matches('[data-add-to-cart-form]')) return;
      e.preventDefault();
      const btn = form.querySelector('[data-atc-btn]');
      const errEl = form.querySelector('[data-atc-error]');
      if (errEl) { errEl.hidden = true; errEl.textContent = ''; }
      if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: form.querySelector('[name=id]').value, quantity: parseInt(form.querySelector('[name=quantity]')?.value || 1) }] })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          // Shopify returns { description, message } on failure (e.g. sold out)
          throw new Error(data.description || data.message || 'Add to cart failed');
        }
        document.dispatchEvent(new Event('cart:updated'));
        // On the cart page (e.g. upsell blocks), reload so line items and totals update
        if (form.hasAttribute('data-reload-after-add')) {
          window.location.reload();
          return;
        }
        window.cartDrawer?.open();
      } catch (err) {
        if (errEl) { errEl.textContent = err.message; errEl.hidden = false; }
        else console.error(err);
      }
      finally { if (btn) { btn.disabled = false; btn.innerHTML = btn.dataset.label || 'Add to cart'; } }
    });
  }
}

// ---- Qty Stepper ----
class QtyStepper {
  constructor(el) {
    this.el = el;
    this.input = el.querySelector('[data-qty-input]');
    el.querySelector('[data-qty-minus]')?.addEventListener('click', () => this.change(-1));
    el.querySelector('[data-qty-plus]')?.addEventListener('click', () => this.change(1));
  }
  change(delta) {
    const curr = parseInt(this.input.value) || 1;
    this.input.value = Math.max(1, curr + delta);
    this.el.querySelector('.qty-stepper__val').textContent = this.input.value;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

// ---- Mobile Nav ----
class MobileNav {
  constructor() {
    this.nav = document.getElementById('mobile-nav');
    this.openBtn = document.getElementById('header-burger');
    this.closeBtn = document.getElementById('mobile-nav-close');
    this.overlay = this.nav?.querySelector('.mobile-nav__overlay');
    if (!this.nav) return;
    this.openBtn?.addEventListener('click', () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
  }
  open() { this.nav.classList.add('is-open'); document.body.style.overflow = 'hidden'; }
  close() { this.nav.classList.remove('is-open'); document.body.style.overflow = ''; }
}

// ---- Collection Filter Drawer (mobile) ----
class FilterDrawer {
  constructor() {
    this.drawer = document.getElementById('CollectionFiltersDrawer');
    this.openBtn = document.getElementById('filters-toggle');
    this.closeBtn = document.getElementById('filters-drawer-close');
    this.overlay = this.drawer?.querySelector('.filters-drawer__overlay');
    if (!this.drawer) return;
    this.openBtn?.addEventListener('click', () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });
  }
  open() {
    this.drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.openBtn?.setAttribute('aria-expanded', 'true');
  }
  close() {
    this.drawer.classList.remove('is-open');
    document.body.style.overflow = '';
    this.openBtn?.setAttribute('aria-expanded', 'false');
  }
}

// ---- Region / Language Selector ----
class LocalizationSelector {
  constructor() {
    // Auto-submit every instance (header popover, mobile nav, footer) on
    // change, and hide their submit buttons once JS has taken over —
    // the button stays as a plain no-JS fallback otherwise.
    document.querySelectorAll('[data-localization-select]').forEach(select => {
      select.addEventListener('change', () => select.form?.submit());
      select.closest('.localization-form')?.classList.add('js-enhanced');
    });

    // Popover behaviour for the header trigger only.
    this.root = document.querySelector('[data-localization-selector]');
    if (!this.root) return;
    this.trigger = this.root.querySelector('.localization-selector__trigger');
    this.panel = this.root.querySelector('.localization-selector__panel');
    if (!this.trigger || !this.panel) return;

    this.trigger.addEventListener('click', () => this.toggle());
    document.addEventListener('click', e => {
      if (!this.root.contains(e.target)) this.close();
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.close();
    });
  }

  toggle() {
    this.root.classList.contains('is-open') ? this.close() : this.open();
  }

  open() {
    this.root.classList.add('is-open');
    this.panel.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.root.classList.remove('is-open');
    this.panel.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
  }
}

// ---- Header Search ----
class HeaderSearch {
  constructor() {
    const wrap = document.querySelector('.header-search');
    const input = wrap?.querySelector('input');
    wrap?.addEventListener('click', () => input?.focus());
  }
}

// ---- PDP Thumbnail Switcher ----
class ThumbSwitcher {
  constructor() {
    const thumbs = document.querySelectorAll('[data-pdp-thumb]');
    const main   = document.querySelector('[data-pdp-main-img]');
    thumbs.forEach(t => t.addEventListener('click', () => {
      thumbs.forEach(x => x.classList.remove('is-active'));
      t.classList.add('is-active');
      if (main && t.dataset.src) main.src = t.dataset.src;
    }));
  }
}

// ---- Custom always-visible scrollbar for PDP thumbnails ----
class ThumbScrollbar {
  constructor() {
    document.querySelectorAll('.pdp-thumbs').forEach(track => this.init(track));
  }

  init(scroller) {
    const bar = document.createElement('div');
    bar.className = 'pdp-thumbs-bar';
    const thumb = document.createElement('div');
    thumb.className = 'pdp-thumbs-bar__thumb';
    bar.appendChild(thumb);
    scroller.after(bar);

    const sync = () => {
      const ratio = scroller.clientWidth / scroller.scrollWidth;
      if (ratio >= 1) { bar.classList.remove('is-scrollable'); return; }
      bar.classList.add('is-scrollable');
      const trackW = bar.clientWidth;
      const thumbW = Math.max(28, trackW * ratio);
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      const maxThumb = trackW - thumbW;
      thumb.style.width = thumbW + 'px';
      thumb.style.transform = `translateX(${maxScroll ? (scroller.scrollLeft / maxScroll) * maxThumb : 0}px)`;
    };

    scroller.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    if (window.ResizeObserver) new ResizeObserver(sync).observe(scroller);
    scroller.querySelectorAll('img').forEach(img => {
      if (!img.complete) img.addEventListener('load', sync, { once: true });
    });

    // Drag the thumb to scroll
    let startX = 0, startScroll = 0;
    const onMove = e => {
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - startX;
      const trackW = bar.clientWidth;
      const maxThumb = trackW - thumb.offsetWidth;
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      if (maxThumb > 0) scroller.scrollLeft = startScroll + (x / maxThumb) * maxScroll;
    };
    const onUp = () => {
      thumb.classList.remove('is-dragging');
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
    };
    const onDown = e => {
      startX = e.touches ? e.touches[0].clientX : e.clientX;
      startScroll = scroller.scrollLeft;
      thumb.classList.add('is-dragging');
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.addEventListener('touchmove', onMove, { passive: true });
      document.addEventListener('touchend', onUp);
      e.preventDefault();
    };
    thumb.addEventListener('mousedown', onDown);
    thumb.addEventListener('touchstart', onDown, { passive: false });

    sync();
  }
}

// ---- Cart Item Qty Updates ----
class CartItemQty {
  constructor() {
    document.addEventListener('click', async e => {
      const btn = e.target.closest('[data-cart-qty]');
      if (!btn) return;
      const line = btn.dataset.line;
      const delta = parseInt(btn.dataset.cartQty);
      const display = btn.closest('.cart-item__stepper')?.querySelector('.cart-item__qty');
      const curr = parseInt(display?.textContent || '1');
      const newQty = Math.max(0, curr + delta);
      if (display) display.textContent = newQty;
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity: newQty })
        });
        document.dispatchEvent(new Event('cart:updated'));
      } catch (e) { console.error(e); }
    });
  }
}

// ---- Init ----
document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawer = new CartDrawer();
  new AddToCart();
  new MobileNav();
  new FilterDrawer();
  new LocalizationSelector();
  new HeaderSearch();
  new ThumbSwitcher();
  new ThumbScrollbar();
  new CartItemQty();
  document.querySelectorAll('[data-qty-stepper]').forEach(el => new QtyStepper(el));
  new AtcPrice();
  new VariantSelector();
});
